import React, { useEffect, useCallback, useRef } from 'react';
import { selectIsAuthenticated } from 'reducers/userSlice';
import { useAppSelector } from 'reducers/hooks';
import { AuthenticationContextType, MsalAuthConfig, AuthenticationProviderProps, RedirectionType } from './types';
import { useAuthentication, redirectToIDP } from './authenticationUtilities';
import { Route } from 'react-router-dom';
import AuthenticationCallbackPage from './AuthenticationCallbackPage';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from "@azure/msal-react";

const LOGOUT_TIMEOUT_IN_MINUTES = 15;
let logoutTimeoutId: NodeJS.Timeout | null = null;

const defaultConfig: MsalAuthConfig = {
  client_id: '',
  authority: '',
  redirect_uri: '',
  post_logout_redirect_uri: '',
  scopes: []
};

export const AuthenticationContext = React.createContext<AuthenticationContextType>({
  handleLoginButtonClick: () => {},
  authConfig: defaultConfig,
  authCallbackConfig: defaultConfig,
  authCallbackRoute: <></>
});

export const AuthenticationProvider = (props: AuthenticationProviderProps) => {
  const { config, children } = props;
  const setAuthorization = useAuthentication();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const instanceRef = useRef<PublicClientApplication>();
  if (!instanceRef.current) {
    instanceRef.current = new PublicClientApplication({
      auth: {
        clientId: config.client_id,
        authority: config.authority,
        knownAuthorities: ['id.milestonelogin.xyz', 'id.milestonelogin.com'],        
        redirectUri: config.redirect_uri,
        postLogoutRedirectUri: config.post_logout_redirect_uri
      },
      cache: {
        cacheLocation: config.cacheLocation || 'sessionStorage',
        storeAuthStateInCookie: config.storeAuthStateInCookie || false
      }
    });
  }

  const instance = instanceRef.current!;

  const initializeAuthentication = useCallback(() => {
    instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS || event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        setAuthorization(config);
      }
    });

    if (!window.location.href.includes(config.redirect_uri)) {
      setAuthorization(config);
    }
  }, [instance, config, setAuthorization]);

  useEffect(() => {
    if (isAuthenticated === null) {
      initializeAuthentication();
    }
  }, [initializeAuthentication, isAuthenticated]);

  useEffect(() => {
    function logoutAfterTimeout() {
      if (logoutTimeoutId) clearTimeout(logoutTimeoutId);
      logoutTimeoutId = setTimeout(
        () => redirectToIDP(config, RedirectionType.Signout),
        LOGOUT_TIMEOUT_IN_MINUTES * 60 * 1000
      );
    }

    window.addEventListener('click', logoutAfterTimeout);
    window.addEventListener('mousemove', logoutAfterTimeout);
    window.addEventListener('keypress', logoutAfterTimeout);

    return () => {
      window.removeEventListener('click', logoutAfterTimeout);
      window.removeEventListener('mousemove', logoutAfterTimeout);
      window.removeEventListener('keypress', logoutAfterTimeout);
      if (logoutTimeoutId) clearTimeout(logoutTimeoutId);
    };
  }, [config]);

  const authContextValue: AuthenticationContextType = {
    handleLoginButtonClick: () =>
      redirectToIDP(config, isAuthenticated ? RedirectionType.Signout : RedirectionType.Signin),
    authConfig: config,
    authCallbackConfig: { ...config, response_mode: 'query' },
    authCallbackRoute: <Route path={new URL(config.redirect_uri).pathname} element={<AuthenticationCallbackPage />} />
  };

  return (
    <MsalProvider instance={instance}>
      <AuthenticationContext.Provider value={authContextValue}>
        {children}
      </AuthenticationContext.Provider>
    </MsalProvider>
  );
};
